import React, { BaseSyntheticEvent, useContext, useEffect, useRef } from 'react';
import { DisplayableImageData } from 'app/interfaces/Image';
import { BasicDisplayOptions, BasicImageRenderer } from 'app/interfaces/ImageRenderer';
import thumbnailImageRendererFactoryContext from 'app/providers/ImageRenderer/ThumbnailImageRendererFactory/context';
import IDisplayableImageEncoderContext from 'app/providers/IDisplayableImageEncoder/context';

type ThumbnailImageRendererProps = {
  image: DisplayableImageData;
  className?: string;
  onRendererRef?: (ref: BasicImageRenderer) => void;
  displayWithAnnotations?: boolean;
} & BasicDisplayOptions;

const preventDefault = (e: BaseSyntheticEvent) => e.preventDefault();

export function ThumbnailImageRenderer({
  image,
  viewport,
  annotations,
  metadata,
  className = '',
  onRendererRef,
  displayWithAnnotations = false,
}: ThumbnailImageRendererProps) {
  const renderer = useRef<BasicImageRenderer>();
  const imageRendererFactory = useContext(thumbnailImageRendererFactoryContext);
  const displayableImageEncoder = useContext(IDisplayableImageEncoderContext);

  const currentRenderer = renderer.current;

  const onRef = (ref: HTMLElement) => {
    if (!ref || renderer.current?.element === ref) return;

    renderer.current = imageRendererFactory?.create(ref, {
      displayableImageEncoder: displayWithAnnotations ? displayableImageEncoder : undefined,
    });
    onRendererRef?.(renderer.current);
  };

  useEffect(() => {
    return () => currentRenderer?.destroy();
  }, [currentRenderer]);

  useEffect(() => {
    if (!image) return;
    renderer.current?.displayImage(image, {
      viewport,
      annotations,
      metadata,
    });
  }, [currentRenderer, image, viewport, annotations]);

  return (
    <div
      ref={onRef}
      className={className}
      data-testid="thumbnail-render-area"
      onContextMenu={preventDefault}
      style={{ width: '100%', height: '100%' }}
    >
      <canvas className="cornerstone-canvas" style={{ objectFit: 'none' }} />
    </div>
  );
}
